/**
 * IDP-1304: shared dropdown UI for the public-app access pickers
 * (CiamGroupPicker, PublicAccessUserPicker).
 *
 * Styling uses JSS classes (`@mui/styles` makeStyles, the same approach as
 * AccessControlField) instead of `sx`. Inside this app's mixed v4/v5 theme the
 * emotion `sx` styles and the Autocomplete default option CSS do not apply
 * reliably — the option icon ended up on its own line and the text fell back
 * to a serif font. Option rows are targeted with `li.MuiAutocomplete-option`
 * under the dropdown Paper so they win over the Autocomplete defaults, and the
 * font family is set explicitly.
 */
import { default as React } from 'react';
import { makeStyles } from '@mui/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const FONT_FAMILY = 'Roboto, Helvetica, Arial, sans-serif';

/** What an option / chip represents — drives icon, colour and tag. */
export type AccessKind = 'group' | 'internal' | 'external' | 'invite';

interface KindStyle {
  Icon: typeof PersonIcon;
  color: string;
  background: string;
  border: string;
  tag: string;
}

export const ACCESS_KIND_STYLES: Record<AccessKind, KindStyle> = {
  group: {
    Icon: GroupsIcon,
    color: '#1565c0',
    background: '#e3f2fd',
    border: '#90caf9',
    tag: 'Group',
  },
  internal: {
    Icon: BusinessIcon,
    color: '#2e7d32',
    background: '#e8f5e9',
    border: '#a5d6a7',
    tag: 'WellSky',
  },
  external: {
    Icon: PersonIcon,
    color: '#6a1b9a',
    background: '#f3e5f5',
    border: '#ce93d8',
    tag: 'External',
  },
  invite: {
    Icon: PersonAddIcon,
    color: '#6a1b9a',
    background: '#f3e5f5',
    border: '#ce93d8',
    tag: 'Invite',
  },
};

const chipStyle = (kind: AccessKind) => {
  const s = ACCESS_KIND_STYLES[kind];
  // '&&' doubles the class selector so these win over MUI's own Chip styles
  // regardless of stylesheet injection order.
  return {
    '&&': {
      height: 28,
      fontFamily: FONT_FAMILY,
      fontSize: '0.8125rem',
      fontWeight: 500,
      backgroundColor: s.background,
      color: s.color,
      border: `1px solid ${s.border}`,
      '& .MuiChip-icon': { color: s.color, marginLeft: 6 },
      '& .MuiChip-deleteIcon': {
        color: s.color,
        opacity: 0.6,
        '&:hover': { color: s.color, opacity: 1 },
      },
    },
  };
};

export const useAccessPickerStyles = makeStyles({
  /** Search input font. Apply to the Autocomplete root via className. */
  input: {
    '& .MuiInputBase-input, & .MuiInputBase-input::placeholder': {
      fontFamily: FONT_FAMILY,
      fontSize: '0.9rem',
    },
  },
  /** Dropdown paper — apply through slotProps.paper.className. */
  paper: {
    '&&': {
      fontFamily: FONT_FAMILY,
      marginTop: 4,
      borderRadius: 8,
      border: '1px solid rgba(0, 0, 0, 0.12)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-listbox': {
      maxHeight: 340,
      padding: 0,
    },
    '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
      fontFamily: FONT_FAMILY,
      fontSize: '0.875rem',
      padding: '12px 16px',
    },
  },
  /**
   * Option row — applied directly on the <li> (not via a descendant selector
   * from the paper, which did not match in this app). '&&' doubles the class
   * so it wins over the Autocomplete default option styles.
   */
  option: {
    '&&': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 12,
      minHeight: 56,
      margin: 0,
      padding: '10px 16px 10px 13px',
      borderLeft: '3px solid transparent',
      cursor: 'pointer',
      listStyle: 'none',
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      fontFamily: FONT_FAMILY,
    },
    '&& + &&': {
      borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    },
    '&&.Mui-focused, &&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      borderLeftColor: 'currentColor',
    },
    '&&[aria-selected="true"]': {
      backgroundColor: 'rgba(0, 0, 0, 0.06)',
    },
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { fontSize: 18 },
  },
  text: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  primary: {
    fontFamily: FONT_FAMILY,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
    color: 'rgba(0, 0, 0, 0.87)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  primaryInvite: {
    fontStyle: 'italic',
  },
  secondary: {
    fontFamily: FONT_FAMILY,
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: 'rgba(0, 0, 0, 0.6)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tag: {
    flexShrink: 0,
    fontFamily: FONT_FAMILY,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    padding: '2px 6px',
    borderRadius: 4,
  },
  groupItem: {
    listStyle: 'none',
  },
  groupHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '6px 16px',
    backgroundColor: '#fafafa',
    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    fontFamily: FONT_FAMILY,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  groupList: {
    padding: 0,
    margin: 0,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 10,
  },
  chipGroup: chipStyle('group'),
  chipInternal: chipStyle('internal'),
  chipExternal: chipStyle('external'),
});

export type AccessPickerClasses = ReturnType<typeof useAccessPickerStyles>;

/** Chip className for a kind, so chips match the option colours. */
export const accessChipClass = (
  classes: AccessPickerClasses,
  kind: AccessKind,
): string => {
  if (kind === 'group') return classes.chipGroup;
  if (kind === 'internal') return classes.chipInternal;
  return classes.chipExternal;
};

/** One dropdown row: coloured icon · name + secondary line · type tag. */
export const AccessPickerOption = ({
  liProps,
  classes,
  kind,
  primary,
  secondary,
}: {
  liProps: React.HTMLAttributes<HTMLLIElement>;
  classes: AccessPickerClasses;
  kind: AccessKind;
  primary: string;
  secondary?: string;
}) => {
  const style = ACCESS_KIND_STYLES[kind];
  const { Icon } = style;
  return (
    <li
      {...liProps}
      className={[liProps.className, classes.option].filter(Boolean).join(' ')}
      style={{ ...liProps.style, color: style.color }}
    >
      <span
        aria-hidden
        className={classes.avatar}
        style={{ backgroundColor: style.background, color: style.color }}
      >
        <Icon />
      </span>
      <span className={classes.text}>
        <span
          className={
            kind === 'invite'
              ? `${classes.primary} ${classes.primaryInvite}`
              : classes.primary
          }
        >
          {primary}
        </span>
        {secondary && <span className={classes.secondary}>{secondary}</span>}
      </span>
      <span
        className={classes.tag}
        style={{ backgroundColor: style.background, color: style.color }}
      >
        {style.tag}
      </span>
    </li>
  );
};

/** Sticky section header for grouped results ("WellSky Users · 3"). */
export const AccessPickerGroupHeader = ({
  classes,
  label,
  count,
  children,
}: {
  classes: AccessPickerClasses;
  label: string;
  count: number;
  children: React.ReactNode;
}) => (
  <li className={classes.groupItem}>
    <div className={classes.groupHeader}>
      {label}
      {count > 0 ? ` · ${count}` : ''}
    </div>
    <ul className={classes.groupList}>{children}</ul>
  </li>
);
